import {
  DecklistInput,
  decklistToTbppPack,
  TbppPack,
} from "@/utils/tableplace/toPack";

/**
 * Provisions a seeded table.place lobby via POST /v1/lobbies.
 * API contract: https://api.table.place/llms.txt
 *
 * Note: the API rejects packs carrying `pieces` (counters, tokens, dice)
 * with 400 unsupported_content, so mana counters and token stacks cannot
 * be seeded yet — players grab those from the table's toolbox in-game.
 */

const API_URL = "https://api.table.place/v1/lobbies";
const SITE_URL = "https://spells.bar";

// Generated by scripts alongside public/tableplace/sorcery-map.webp:
// a 5×4 realm grid, 14.1 × 10.5 world units (cards are 1.4 × 2).
const MAP_URL = `${SITE_URL}/tableplace/sorcery-map.webp`;
const MAP_RATIO = 1.3429;
const MAP_SCALE = 14.1;

type TbppTablePack = Omit<TbppPack, "scope" | "decks"> & {
  scope: "table";
  decks: [];
  overlays: { imageUrl: string; ratio: number; scale: number }[];
};

const realmMapPack: TbppTablePack = {
  $schema: "https://table.place/pack.schema.json",
  tbpp: 1,
  specVersion: "1.3.0",
  id: "spellsbar-realm-map",
  name: "The Realm",
  scope: "table",
  decks: [],
  overlays: [{ imageUrl: MAP_URL, ratio: MAP_RATIO, scale: MAP_SCALE }],
};

export type LobbySeat = { seat: number; url: string };
export type LobbyResult = {
  lobby: string;
  lobby_url: string;
  seats: LobbySeat[];
  expires_at: string;
};

export class LobbyError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
  }
}

export const createLobby = async (
  seat0: DecklistInput,
  seat1: DecklistInput,
  cardNames?: Record<string, string>,
  opts?: { ttlSeconds?: number },
): Promise<LobbyResult> => {
  const packs = [
    decklistToTbppPack(seat0, cardNames),
    decklistToTbppPack(seat1, cardNames),
  ];
  // Pack ids must be distinct even when both seats play the same deck
  packs.forEach((pack, seat) => {
    pack.id = `${pack.id}-seat${seat}`;
  });

  let res: Response;
  try {
    res = await fetch(API_URL, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        version: 1,
        layout: "sorcery-2p",
        packs: [...packs, realmMapPack],
        ttlSeconds: opts?.ttlSeconds ?? 14400,
      }),
    });
  } catch {
    throw new LobbyError("network", "Could not reach api.table.place");
  }

  const body = await res.json().catch(() => null);
  if (!res.ok) {
    throw new LobbyError(
      body?.error ?? `http_${res.status}`,
      body?.message ?? `Lobby creation failed (HTTP ${res.status})`,
    );
  }
  return body as LobbyResult;
};
